import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';

import colors from '../utils/colors';

const AboutBody = styled.div`
  a {
    color: ${colors.accent1()};
    transition: all 0.5s;
    &:hover {
      background-color: ${colors.accent1(0.1)};
    }
  }
  margin-bottom: 60px;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionHeader = styled.h1`
  margin-top: 50px;
  margin-bottom: 20px;
  text-transform: uppercase;
  color: ${colors.base1()};
  font-size: 22px;
  letter-spacing: 2px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${colors.shade1()};
`;

const BioText = styled.p`
  font-size: 16px;
  line-height: 30px;
  color: ${colors.base2()};
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 10px;
  margin-top: 25px;
`;

const EduGrid = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;

  @media only screen and (max-width: 48em) {
    flex-direction: column;
    gap: 16px;
  }
`;

const EduCard = styled.div`
  flex: 1;
  padding: 24px;
  border: 1px solid ${colors.shade1()};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const EduDegree = styled.h3`
  margin: 0 0 6px 0;
  color: ${colors.accent1()};
  font-size: 17px;
`;

const EduYear = styled.span`
  color: ${colors.base3()};
  font-weight: normal;
  font-size: 14px;
`;

const EduInstitution = styled.p`
  margin: 0 0 8px 0;
  color: ${colors.base3()};
  font-size: 14px;
  font-family: 'Open Sans', sans-serif;
`;

const EduGpa = styled.p`
  margin: 0 0 10px 0;
  color: ${colors.accent2()};
  font-weight: 600;
  font-size: 14px;
  font-family: 'Open Sans', sans-serif;
`;

const EduCapstone = styled.p`
  margin: 0;
  color: ${colors.base2()};
  font-size: 13px;
  font-style: italic;
  font-family: 'Open Sans', sans-serif;
  line-height: 20px;
`;

const PillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const ResearchPill = styled.span`
  display: inline-block;
  padding: 8px 18px;
  border-radius: 24px;
  font-size: 13px;
  font-family: 'Open Sans', sans-serif;
  background-color: ${colors.accent3()};
  color: ${colors.accent1()};
  font-weight: 500;
`;

const AwardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: #f7f9fb;
  border-radius: 6px;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  line-height: 20px;
`;

const AwardTitle = styled.span`
  color: ${colors.base2()};
`;

const AwardYear = styled.span`
  color: ${colors.base3()};
  font-size: 13px;
  flex-shrink: 0;
  margin-left: 16px;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 40px;
  margin-top: 8px;

  @media only screen and (max-width: 48em) {
    flex-direction: column;
    gap: 0;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
`;

class AboutPage extends React.Component {
  componentDidMount() {
    this.node.style.opacity = 0;
    window.requestAnimationFrame(() => {
      this.node.style.transition = 'opacity 1000ms ease-out';
      this.node.style.opacity = 1;
    });
  }

  render() {
    const education = [
      {
        degree: 'M.Sc. in Cybersecurity',
        institution: 'City College of New York (CUNY)',
        gpa: 'Summa Cum Laude',
        year: 'Jan 2025',
        capstone: 'Adaptive Clipping and Noise Techniques for Privacy-Preserving ML with Class Imbalance',
      },
      {
        degree: 'B.Sc. in Computer Science',
        institution: 'City College of New York (CUNY)',
        gpa: 'Magna Cum Laude',
        year: 'Jun 2021',
        capstone: 'Decentralized Voter Analytics using Hypercube Networks and Heterogeneous Databases',
      },
    ];

    const researchAreas = [
      'Differential Privacy and DP-SGD',
      'Privacy-Preserving Machine Learning',
      'Threshold Cryptography and Proxy Re-encryption',
      'Zero-Knowledge Proofs and Verifiable Computation',
      'Confidential Computing and Attestation',
    ];

    const awards = [
      { title: 'Best Presentation Award, IEEE UEMCON', year: '2021', url: 'https://ieeexplore.ieee.org/abstract/document/9666482' },
      { title: 'NSF Support Grant for MIT CSAIL Workshop', year: '2024', url: 'https://computersciencelaw.org/2024/workshop-for-junior-scholars/' },
      { title: 'Google Cloud Research Credits ($5K)', year: '2025', url: null },
      { title: 'Zahn First-Place Seed Grant ($30K)', year: '2022', url: 'https://www.ccny.cuny.edu/zahn/blog/congratulations-2022-incubator-competition-winners' },
      { title: 'NSF I-Corps Best Presentation Award', year: '2022', url: 'https://www.nycinnovationhotspot.org/gotham-innovation-gambit' },
      { title: 'Zahn Seed Grant ($28K)', year: '2020', url: 'https://www.ccny.cuny.edu/zahn/blog/ccny-startups-take-home-150000-prizes-2020-final-pitch-night' },
      { title: 'Columbia IBM Launch Accelerator ($2.5K)', year: '2019', url: 'https://vimeo.com/343229618' },
      { title: 'ORCA Research Fellowship, CCNY', year: '2020', url: 'https://orca2020cohort.commons.gc.cuny.edu/2020/10/07/predicting-type-2-diabetes-using-machine-learning/' },
    ];

    const service = [
      { role: 'Student Advisory Board', org: 'CUNY Startups', year: '2024-25', url: 'https://cunystartups.com/student-advisory-board-m/' },
      { role: 'Judge', org: 'Zahn Innovation Center', year: '2024', url: 'https://www.ccny.cuny.edu/zahn' },
      { role: 'Attendee (NSF-supported)', org: 'ACM CS & Law Workshop', year: '2024', url: 'https://computersciencelaw.org/2024-2/' },
      { role: 'Co-Founder', org: 'CCNY Crypto Club (120+ members)', year: '2018-19', url: null },
    ];

    return (
      <AboutBody ref={(node) => { this.node = node; }}>
        <Helmet>
          <title>About - Kartikeya Sharma</title>
        </Helmet>

        <BioText>
          I specialize in privacy-enhancing technologies and the applied cryptography that
          makes them practically deployable, focusing on differential privacy, threshold
          protocols, and verifiable computation. My work bridges the gap between
          theoretical guarantees and production realities, whether that means engineering
          secure AI guardrails or exposing hidden flaws in standard privacy assumptions.
        </BioText>
        <BioText>
          In my independent research on differentially private learning for imbalanced
          clinical data, I found that standard approaches often fail quietly. Uniform
          gradient clipping is not class-neutral: it systematically suppresses minority
          classes, with median per-class gradient norms diverging by a factor of 7.75.
          Furthermore, common remedies like SMOTE silently break record-level privacy
          guarantees by clustering synthetic and real patients into correlated groups. To
          fix this, I introduced per-class adaptive bounds that improved AUC without
          diverging across thousands of runs, demonstrating that the assumed
          privacy-utility tradeoff is not always a strict zero-sum game. The working paper
          is <Link to="/research">on the research page</Link>, with code at{' '}
          <a href="https://github.com/sha256rma/dp-diabetes-prediction" target="_blank" rel="noopener noreferrer">
            github.com/sha256rma/dp-diabetes-prediction
          </a>.
        </BioText>
        <BioText>
          I am equally focused on the architectural deployment of secure systems. During my
          graduate studies at the City College of New York, I published six peer-reviewed
          papers (earning a Best Presentation Award at IEEE UEMCON 2021) on
          patient-controlled health record sharing. I acted as the sole implementer for a
          system combining threshold proxy re-encryption, Ethereum access-control
          contracts, and multi-node private IPFS. Beyond patching vulnerabilities via
          Slither pre-publication, I navigated the deployment through Ethereum&apos;s
          proof-of-work to proof-of-stake transition, re-deriving timing logic to achieve a
          nearly 80% latency reduction. Rather than hiding system limitations, I explicitly
          documented the friction points of real-world cryptography, such as the
          forward-only nature of revocation and the inherent metadata leakage of
          relationship graphs on-chain.
        </BioText>
        <BioText>
          In the industry, I recently owned a production guardrail suite of seven detection
          models as an AI Security Engineer at Tumeryk. I cut mean Safety Check latency by
          58% with zero accuracy loss by migrating serving to vLLM on GCP, and drastically
          reduced bias false positives from 25% down to 2%. I also built a Garak-based
          red-teaming harness, exposing severe reproducibility issues in LLM-as-judge
          evaluations where attack-pass rates varied from 6% to 91% based solely on judge
          configuration, and authored formal public comments proposing MITRE ATLAS
          integration for NIST IR 8596.
        </BioText>
        <BioText>
          My engineering is grounded in deep academic roots, including studying PhD-level
          Modern Cryptography and Zero-Knowledge Proofs. Today, I actively share that
          foundation by teaching the cryptographic primitives of Bitcoin, from one-way
          functions to threshold signing with FROST, as a Bitshala Educator Fellow.
        </BioText>

        <SectionHeader>Education</SectionHeader>
        <EduGrid>
          {education.map((edu) => (
            <EduCard key={edu.degree}>
              <EduDegree>
                {edu.degree} <EduYear>({edu.year})</EduYear>
              </EduDegree>
              <EduInstitution>{edu.institution}</EduInstitution>
              <EduGpa>{edu.gpa}</EduGpa>
              <EduCapstone>Capstone: {edu.capstone}</EduCapstone>
            </EduCard>
          ))}
        </EduGrid>

        <SectionHeader>Research Areas</SectionHeader>
        <PillContainer>
          {researchAreas.map((area) => (
            <ResearchPill key={area}>{area}</ResearchPill>
          ))}
        </PillContainer>

        <TwoColumn>
          <Column>
            <SectionHeader>Awards & Grants</SectionHeader>
            {awards.map((award) => (
              <AwardRow key={award.title}>
                <AwardTitle>
                  {award.url ? (
                    <a href={award.url} target="_blank" rel="noopener noreferrer">{award.title}</a>
                  ) : award.title}
                </AwardTitle>
                <AwardYear>{award.year}</AwardYear>
              </AwardRow>
            ))}
          </Column>
          <Column>
            <SectionHeader>Service & Leadership</SectionHeader>
            {service.map((item) => (
              <AwardRow key={item.role + item.org}>
                <AwardTitle>
                  {item.role} &middot;{' '}
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer">{item.org}</a>
                  ) : item.org}
                </AwardTitle>
                <AwardYear>{item.year}</AwardYear>
              </AwardRow>
            ))}
          </Column>
        </TwoColumn>
      </AboutBody>
    );
  }
}

export default AboutPage;
